import { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  DEMO,
  ApiFehler,
  SITZUNG_ABGELAUFEN,
  type Benutzer,
  type Mandant,
  abmelden,
  anmeldungAusAdresseUebernehmen,
  api,
  getToken,
  ich,
} from './lib/api';
import { TabBar } from './components/TabBar';
import { MehrBlatt } from './components/MehrBlatt';
import type { TabDefinition } from './components/TabBar';
import { Button } from './components/ui/Button';
import {
  IconAbrechnungen,
  IconAdmin,
  IconAkte,
  IconAuswertung,
  IconBelege,
  IconEinstellungen,
  IconGlocke,
  IconMangel,
  IconMassnahme,
  IconMehr,
  IconMond,
  IconObjekte,
  IconRechner,
  IconSchild,
  IconSonne,
  IconSteuer,
  IconWartung,
} from './components/icons';
import { Bildmarke, Schriftzug } from './components/Marke';
import { useDarstellung } from './lib/darstellung';
import { seitenVarianten, transition, uebergangSanft } from './lib/motion';
import Login from './pages/Login';
import KontoEinrichtung from './pages/KontoEinrichtung';
import Meldeseite from './pages/Meldeseite';
import Startseite from './pages/Startseite';
import Objekte, { type ObjektFokus } from './pages/Objekte';
import Anlagenbuch from './pages/Anlagenbuch';
import Massnahmen from './pages/Massnahmen';
import Maengel from './pages/Maengel';
import Dokumente from './pages/Dokumente';
import Abrechnungen from './pages/Abrechnungen';
import Belege from './pages/Belege';
import Auswertung from './pages/Auswertung';
import Rechner from './pages/Rechner';
import Steuer from './pages/Steuer';
import Admin from './pages/Admin';
import { Einstellungen } from './pages/Einstellungen';
import AbrechnungWizard from './pages/AbrechnungWizard';

type TabId =
  | 'start'
  | 'objekte'
  | 'anlagenbuch'
  | 'massnahmen'
  | 'maengel'
  | 'abrechnungen'
  | 'belege'
  | 'dokumente'
  | 'auswertung'
  | 'rechner'
  | 'steuer'
  | 'admin'
  | 'einstellungen';

const ADMIN_TAB: TabDefinition = { id: 'admin', label: 'Administration', kurzLabel: 'Admin', icon: <IconAdmin groesse={20} /> };

const TABS: TabDefinition[] = [
  { id: 'start', label: 'Übersicht', kurzLabel: 'Start', icon: <IconSchild groesse={20} /> },
  { id: 'objekte', label: 'Objekte', icon: <IconObjekte groesse={20} /> },
  { id: 'anlagenbuch', label: 'Anlagenbuch', kurzLabel: 'Anlagen', icon: <IconWartung groesse={20} /> },
  { id: 'massnahmen', label: 'Maßnahmen', icon: <IconMassnahme groesse={20} /> },
  { id: 'maengel', label: 'Mängelmelder', kurzLabel: 'Mängel', icon: <IconMangel groesse={20} /> },
  { id: 'abrechnungen', label: 'Abrechnungen', kurzLabel: 'Abrechnung', icon: <IconAbrechnungen groesse={20} /> },
  { id: 'belege', label: 'Belege', icon: <IconBelege groesse={20} /> },
  { id: 'dokumente', label: 'Dokumente', icon: <IconAkte groesse={20} /> },
  { id: 'auswertung', label: 'Auswertung', icon: <IconAuswertung groesse={20} /> },
  { id: 'rechner', label: 'Rechner', icon: <IconRechner groesse={20} /> },
  { id: 'steuer', label: 'Steuer', icon: <IconSteuer groesse={20} /> },
  { id: 'einstellungen', label: 'Einstellungen', icon: <IconEinstellungen groesse={20} /> },
];

/**
 * Untere Leiste auf kleinen Geräten: höchstens fünf Einträge, sonst werden die Beschriftungen
 * unlesbar. Die vier Hauptbereiche stehen direkt in der Leiste — die beiden Säulen
 * (Anlagenbuch, Abrechnungen) plus Einstieg und Objekte. Alles Weitere liegt hinter „Mehr".
 */
const HANDY_HAUPTBEREICHE: TabId[] = ['start', 'objekte', 'anlagenbuch', 'abrechnungen'];
const MEHR_TAB: TabDefinition = { id: 'mehr', label: 'Mehr', icon: <IconMehr groesse={20} /> };

/** Öffentlicher Mängel-Meldelink /melden/:token — ohne Anmeldung, außerhalb der App-Shell. */
function meldeToken(): string | null {
  const treffer = /^\/melden\/([^/?#]+)/.exec(window.location.pathname);
  return treffer ? decodeURIComponent(treffer[1]) : null;
}

export function App() {
  // Der Meldelink ist eine eigenständige, anmeldefreie Seite außerhalb der App-Shell.
  // Vor allen Hooks der Haupt-App abfangen, damit deren Sitzungslogik gar nicht erst läuft.
  const token = meldeToken();
  if (token) return <Meldeseite token={token} />;
  return <HauptApp />;
}

interface Konto {
  benutzer: Benutzer;
  mandant: Mandant;
}

/** Beispielkonto der Vorschau (Demo-Build) — keine echten Personendaten. */
const DEMO_KONTO: Konto = {
  benutzer: {
    id: 'vorschau',
    email: 'vorschau@grundpuls.de',
    name: 'Vorschau',
    vorname: null,
    nachname: null,
    rolle: 'admin',
    istAdmin: true,
    passwortGesetzt: true,
    passwortAendern: false,
    onboardingErforderlich: false,
    googleVerknuepft: false,
  },
  mandant: { id: 'vorschau', name: 'Vorschau', typ: 'privatvermieter', stufe: 1, testphaseBis: new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10) },
};

function HauptApp() {
  const darstellung = useDarstellung();
  const reduziert = useReducedMotion();

  const [konto, setKonto] = useState<Konto | null>(null);
  const [tab, setTab] = useState<TabId>('start');
  const [wizardOffen, setWizardOffen] = useState(false);
  const [mehrOffen, setMehrOffen] = useState(false);
  // Objekt gezielt öffnen (z. B. Zähler aus der Übersicht) — Haus im Mittelpunkt.
  const [objektFokus, setObjektFokus] = useState<ObjektFokus | null>(null);
  const [objekteNeu, setObjekteNeu] = useState(0);
  // Beim Start eine bestehende Sitzung wiederherstellen (Token aus der letzten Anmeldung).
  const [pruefeSitzung, setPruefeSitzung] = useState(!DEMO);
  // Server nach mehreren Versuchen nicht erreichbar: angemeldet bleiben, erneut versuchen lassen.
  const [verbindungsfehler, setVerbindungsfehler] = useState(false);
  const [anmeldefehler, setAnmeldefehler] = useState<string | null>(null);
  const [sitzungAbgelaufen, setSitzungAbgelaufen] = useState(false);

  /** Konto laden. Nur 401 meldet ab (apiFetch löscht dann den Token); 503/Netz nicht. */
  async function kontoLaden(): Promise<void> {
    if (DEMO) {
      setKonto((k) => k ?? DEMO_KONTO);
      return;
    }
    if (!getToken()) {
      setKonto(null);
      return;
    }
    try {
      const r = await ich();
      setKonto(r);
      setVerbindungsfehler(false);
    } catch (e) {
      if (e instanceof ApiFehler && e.status === 401) setKonto(null);
      else setVerbindungsfehler(true);
    }
  }

  useEffect(() => {
    if (DEMO) return;
    let aktiv = true;
    (async () => {
      // Rückkehr von Google: Einmal-Code gegen den Sitzungstoken tauschen — vor der Sitzungsprüfung.
      const fehler = await anmeldungAusAdresseUebernehmen();
      if (!aktiv) return;
      setAnmeldefehler(fehler);
      await kontoLaden();
      if (aktiv) setPruefeSitzung(false);
    })();
    return () => {
      aktiv = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sitzung vom Server abgelehnt (abgelaufen, auf allen Geräten abgemeldet, Passwort geändert).
  useEffect(() => {
    const beiAblauf = () => {
      setKonto(null);
      setSitzungAbgelaufen(true);
      setWizardOffen(false);
      setTab('start');
    };
    window.addEventListener(SITZUNG_ABGELAUFEN, beiAblauf);
    return () => window.removeEventListener(SITZUNG_ABGELAUFEN, beiAblauf);
  }, []);

  function zuruecksetzen() {
    setKonto(null);
    setSitzungAbgelaufen(false);
    setAnmeldefehler(null);
    setTab('start');
    setWizardOffen(false);
    setMehrOffen(false);
  }

  function abmeldenUndZuruecksetzen() {
    void abmelden().finally(zuruecksetzen);
  }

  if (pruefeSitzung || (verbindungsfehler && !konto)) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-bg px-gutter text-center text-fg-muted">
        <Bildmarke groesse={48} puls="dezent" className="text-fg" />
        {verbindungsfehler ? (
          <>
            <p className="max-w-xs text-sm">
              Der Server ist derzeit nicht erreichbar. Ihre Anmeldung bleibt erhalten.
            </p>
            <Button
              variante="sekundaer"
              onClick={() => {
                setVerbindungsfehler(false);
                setPruefeSitzung(true);
                void kontoLaden().finally(() => setPruefeSitzung(false));
              }}
            >
              Erneut versuchen
            </Button>
          </>
        ) : (
          <span className="text-sm">Sitzung wird geprüft …</span>
        )}
      </div>
    );
  }

  if (!konto) {
    return (
      <Login
        anmeldefehler={anmeldefehler}
        sitzungAbgelaufen={sitzungAbgelaufen}
        onAnmeldung={() => {
          setSitzungAbgelaufen(false);
          setAnmeldefehler(null);
          if (DEMO) setKonto({ ...DEMO_KONTO, benutzer: { ...DEMO_KONTO.benutzer, onboardingErforderlich: true } });
          else void kontoLaden();
        }}
      />
    );
  }

  const { benutzer } = konto;
  const email = benutzer.email;
  const istAdmin = DEMO || !!benutzer.istAdmin;

  if (benutzer.passwortAendern || benutzer.onboardingErforderlich) {
    return (
      <KontoEinrichtung
        benutzer={benutzer}
        mandant={konto.mandant}
        onAbmelden={abmeldenUndZuruecksetzen}
        onFertig={() => {
          if (DEMO) {
            setKonto({
              ...konto,
              benutzer: { ...benutzer, onboardingErforderlich: false, passwortAendern: false },
            });
          } else void kontoLaden();
        }}
      />
    );
  }

  function wechsle(id: string) {
    // Erneuter Klick auf „Objekte“ führt aus einer geöffneten Objekt- oder Einheitenakte zurück zur Liste.
    if (id === 'objekte') setObjekteNeu((n) => n + 1);
    setObjektFokus(null);
    setTab(id as TabId);
    setWizardOffen(false);
    setMehrOffen(false);
  }

  function oeffneZaehler(objektId: string) {
    setObjektFokus({ objektId, bereich: 'zaehler', nonce: Date.now() });
    setTab('objekte');
    setWizardOffen(false);
    setMehrOffen(false);
  }

  const sichtbareTabs = istAdmin ? [...TABS, ADMIN_TAB] : TABS;
  const inhaltsSchluessel = wizardOffen ? 'wizard' : tab;

  const handyTabs = [
    ...sichtbareTabs.filter((t) => HANDY_HAUPTBEREICHE.includes(t.id as TabId)),
    MEHR_TAB,
  ];
  const handyWeitere = sichtbareTabs.filter((t) => !HANDY_HAUPTBEREICHE.includes(t.id as TabId));
  const handyAktiv = mehrOffen || !HANDY_HAUPTBEREICHE.includes(tab) ? 'mehr' : tab;

  return (
    <div className="min-h-dvh bg-bg text-fg">
      <div className="mx-auto flex w-full max-w-content">
        {/* Seitennavigation ab Tablet */}
        <aside className="sticky top-0 hidden h-dvh w-sidebar shrink-0 flex-col border-r border-border bg-surface px-3 py-4 md:flex">
          <div className="mb-5 flex items-center gap-3 px-1">
            <Bildmarke groesse={36} puls="dezent" className="text-fg" />
            <div className="min-w-0">
              <p className="truncate text-sm"><Schriftzug /></p>
              <p className="truncate text-xs text-fg-muted">Betriebskostenabrechnung</p>
            </div>
          </div>

          <TabBar
            tabs={sichtbareTabs}
            aktiv={tab}
            onWechsel={wechsle}
            ausrichtung="vertikal"
            ariaLabel="Hauptnavigation"
            panelId="hauptbereich"
          />

          <div className="mt-auto px-1 pt-4 text-xs text-fg-subtle">
            <p className="truncate">{email}</p>
          </div>
        </aside>

        {/* Hauptbereich */}
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-10 border-b border-border bg-surface/90 backdrop-blur">
            <div className="flex items-center justify-between gap-3 px-gutter py-3">
              <div className="flex items-center gap-2 md:hidden">
                <Bildmarke groesse={32} puls="dezent" className="text-fg" />
                <Schriftzug className="text-sm" />
              </div>

              <p className="hidden text-sm text-fg-muted md:block">
                Abrechnungszeitraum 2025 · {sichtbareTabs.find((t) => t.id === tab)?.label}
              </p>

              <div className="flex items-center gap-1">
                {istAdmin && <AdminGlocke onOeffnen={() => wechsle('admin')} />}
                <Button
                  variante="dezent"
                  groesse="sm"
                  onClick={darstellung.umschalten}
                  aria-label={
                    darstellung.istDunkel
                      ? 'Zur hellen Darstellung wechseln'
                      : 'Zur dunklen Darstellung wechseln'
                  }
                  icon={
                    darstellung.istDunkel ? <IconSonne groesse={18} /> : <IconMond groesse={18} />
                  }
                />
              </div>
            </div>
          </header>

          <main
            id="hauptbereich"
            role="tabpanel"
            aria-label={sichtbareTabs.find((t) => t.id === tab)?.label ?? 'Inhalt'}
            className="flex-1 px-gutter pb-tabbar pt-5 md:pb-8"
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={inhaltsSchluessel}
                variants={reduziert ? undefined : seitenVarianten}
                initial="eingang"
                animate="aktiv"
                exit="ausgang"
                transition={transition(!!reduziert, uebergangSanft)}
              >
                {wizardOffen ? (
                  <AbrechnungWizard
                    onFertig={() => setWizardOffen(false)}
                    onAbbruch={() => setWizardOffen(false)}
                  />
                ) : (
                  <Seiteninhalt
                    tab={tab}
                    benutzer={benutzer}
                    darstellung={darstellung}
                    onWechselTab={wechsle}
                    onNeueAbrechnung={() => setWizardOffen(true)}
                    onAbmelden={abmeldenUndZuruecksetzen}
                    onUeberallAbgemeldet={zuruecksetzen}
                    onKontoAktualisieren={kontoLaden}
                    onOeffneZaehler={oeffneZaehler}
                    objektFokus={objektFokus}
                    objekteSchluessel={objekteNeu}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </main>
        </div>
      </div>

      {/* Tableiste auf kleinen Geräten */}
      <nav className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-surface/95 backdrop-blur sicherheitsabstand-unten md:hidden">
        <div className="px-2 py-1.5">
          <TabBar
            tabs={handyTabs}
            aktiv={handyAktiv}
            onWechsel={(id) => (id === 'mehr' ? setMehrOffen((o) => !o) : wechsle(id))}
            ariaLabel="Hauptnavigation"
            panelId="hauptbereich"
          />
        </div>
      </nav>

      <MehrBlatt
        offen={mehrOffen}
        bereiche={handyWeitere}
        aktiv={tab}
        onWahl={wechsle}
        onSchliessen={() => setMehrOffen(false)}
      />
    </div>
  );
}

function Seiteninhalt({
  tab,
  benutzer,
  darstellung,
  onWechselTab,
  onNeueAbrechnung,
  onAbmelden,
  onUeberallAbgemeldet,
  onKontoAktualisieren,
  onOeffneZaehler,
  objektFokus,
  objekteSchluessel,
}: {
  tab: TabId;
  benutzer: Benutzer;
  darstellung: ReturnType<typeof useDarstellung>;
  onWechselTab: (id: string) => void;
  onNeueAbrechnung: () => void;
  onAbmelden: () => void;
  onUeberallAbgemeldet: () => void;
  onKontoAktualisieren: () => Promise<void>;
  onOeffneZaehler: (objektId: string) => void;
  objektFokus: ObjektFokus | null;
  objekteSchluessel: number;
}) {
  switch (tab) {
    case 'start':
      return <Startseite onOeffneAbrechnungen={() => onWechselTab('abrechnungen')} onOeffneZaehler={onOeffneZaehler} />;
    case 'objekte':
      return (
        <Objekte
          key={objekteSchluessel}
          fokus={objektFokus}
          onOeffneAbrechnungen={() => onWechselTab('abrechnungen')}
        />
      );
    case 'anlagenbuch':
      return <Anlagenbuch />;
    case 'massnahmen':
      return <Massnahmen />;
    case 'maengel':
      return <Maengel />;
    case 'abrechnungen':
      return <Abrechnungen onNeueAbrechnung={onNeueAbrechnung} />;
    case 'belege':
      return <Belege />;
    case 'dokumente':
      return <Dokumente />;
    case 'auswertung':
      return <Auswertung />;
    case 'rechner':
      return <Rechner />;
    case 'steuer':
      return <Steuer />;
    case 'admin':
      return <Admin />;
    case 'einstellungen':
      return (
        <Einstellungen
          benutzer={benutzer}
          darstellung={darstellung}
          onAbmelden={onAbmelden}
          onUeberallAbgemeldet={onUeberallAbgemeldet}
          onKontoAktualisieren={onKontoAktualisieren}
        />
      );
  }
}

/** Support-Glocke im Kopf, nur für den Plattform-Admin. Zeigt die Zahl neuer Anfragen. */
function AdminGlocke({ onOeffnen }: { onOeffnen: () => void }) {
  const [anzahl, setAnzahl] = useState(DEMO ? 2 : 0);

  useEffect(() => {
    if (DEMO) return;
    let aktiv = true;
    const laden = () =>
      api
        .get<{ ungelesen: number }>('/admin/support/anzahl')
        .then((d) => aktiv && setAnzahl(d.ungelesen))
        .catch(() => {});
    laden();
    const t = setInterval(laden, 30000); // alle 30 s pollen
    return () => {
      aktiv = false;
      clearInterval(t);
    };
  }, []);

  return (
    <button
      onClick={onOeffnen}
      aria-label={anzahl > 0 ? `${anzahl} neue Support-Anfragen` : 'Support-Anfragen'}
      className="relative flex h-9 w-9 items-center justify-center rounded-md text-fg-muted transition hover:bg-surface-2 hover:text-fg"
    >
      <IconGlocke groesse={18} />
      {anzahl > 0 && (
        <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-pill bg-accent px-1 text-[10px] font-bold text-accent-fg">
          {anzahl > 9 ? '9+' : anzahl}
        </span>
      )}
    </button>
  );
}

export default App;
